import { useCallback, useEffect, useRef, useState } from 'react';
import {
    DeviceTestCallStatusData,
    DeviceTestSmsStatusData,
    DeviceTestStatusResponseData
} from '@/api';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getAppCheckTokenHeaders } from '@/firebase';
import { useInterval } from '@/common/hooks';
import { useDevicesTranslation } from '../hooks';

type CallStatus = DeviceTestCallStatusData['status'];
type SmsStatus = DeviceTestSmsStatusData['status'];

const CALL_FINAL: NonNullable<CallStatus>[] = [
    'completed',
    'busy',
    'no-answer',
    'failed',
    'canceled'
];

const SMS_FINAL: NonNullable<SmsStatus>[] = [
    'delivered',
    'undelivered',
    'failed',
    'canceled',
    'response_received',
    'response_timed_out'
];

function isCallFinal(status: CallStatus): boolean {
    return status !== null && CALL_FINAL.includes(status);
}

function isSmsFinal(status: SmsStatus): boolean {
    return status !== null && SMS_FINAL.includes(status);
}

/**
 * The progress of a test call and SMS, from the moment `statusId` arrives until
 * both legs reach a final state. Polled on an interval rather than through
 * React Query: the result is a short-lived progress report for one modal, not
 * something any other screen reads, so it is held in state and dropped when the
 * test ends.
 */
export function useTestDeviceStatus(params: {
    deviceId: string;
    statusId: string | null;
}): {
    status: DeviceTestStatusResponseData | null;
    error: string;
} {
    const { deviceId, statusId } = params;
    const { t } = useDevicesTranslation();
    // The client is swapped out on token refresh, so never capture it: read
    // `openApiRef.current` at call time or you keep using a dead token.
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const [status, setStatus] = useState<DeviceTestStatusResponseData | null>(
        null
    );
    const statusRef = useRef<DeviceTestStatusResponseData | null>(null);
    statusRef.current = status;

    const [error, setError] = useState('');
    const [polling, setPolling] = useState(false);

    useEffect(() => {
        if (statusId) {
            setStatus(null);
            setError('');
            setPolling(true);
        } else {
            setStatus(null);
            setError('');
            setPolling(false);
        }
    }, [statusId]);

    const poll = useCallback(
        (intervalId: NodeJS.Timeout) => {
            if (!statusId) return;

            const prev = statusRef.current;
            const query: {
                pollCall?: string;
                pollSms?: string;
            } = {};

            if (prev) {
                if (isCallFinal(prev.call.status)) {
                    query.pollCall = 'false';
                }
                if (isSmsFinal(prev.sms.status)) {
                    query.pollSms = 'false';
                }
            }

            getAppCheckTokenHeaders()
                .then((headers) =>
                    openApiRef.current.devices.getDeviceTestStatus(
                        deviceId,
                        statusId,
                        query,
                        { headers }
                    )
                )
                .then((response) => {
                    const data: DeviceTestStatusResponseData =
                        response.data.data;
                    const merged: DeviceTestStatusResponseData = {
                        call:
                            data.call.status !== null
                                ? data.call
                                : (prev?.call ?? data.call),
                        sms:
                            data.sms.status !== null
                                ? data.sms
                                : (prev?.sms ?? data.sms)
                    };
                    setStatus(merged);

                    const allFinal =
                        isCallFinal(merged.call.status) &&
                        isSmsFinal(merged.sms.status);

                    if (allFinal) {
                        clearInterval(intervalId);
                        setPolling(false);
                    }
                })
                .catch(() => {
                    setError(t('shared.errors.generic'));
                    clearInterval(intervalId);
                    setPolling(false);
                });
        },
        [deviceId, statusId, t]
    );

    useInterval(poll, 5000, [polling]);

    return { status, error };
}
